import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchClients, createClient, fetchFacilities } from '../services/api';
import './Admin.css';
import './Clients.css';

const EMPTY_FORM = {
  facility_id: '', name: '', invoice_prefix: '',
  contact_name: '', contact_email: '', contact_phone: '',
  address: '', city: '', state: '', zip: '',
  payment_terms: 'Net 30', notes: '',
};

function fmtCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);
}

function fmtDate(val) {
  if (!val) return null;
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function autoPrefix(name) {
  if (!name) return '';
  return name
    .split(/\s+/)
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 6);
}

function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [prefixAutoSet, setPrefixAutoSet] = useState(true);

  const load = useCallback(async () => {
    try {
      const [clientsRes, facilitiesRes] = await Promise.all([fetchClients(), fetchFacilities()]);
      setClients(clientsRes.data || []);
      setFacilities(facilitiesRes.facilities || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const matched = !q ? clients : clients.filter(c =>
      c.name.toLowerCase().includes(q) ||
      (c.facility_name || '').toLowerCase().includes(q) ||
      (c.invoice_prefix || '').toLowerCase().includes(q)
    );
    // Newest first: client_id is auto-incrementing, so higher = more recent
    return [...matched].sort((a, b) => b.client_id - a.client_id);
  }, [clients, searchQuery]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleNameChange = (val) => {
    setField('name', val);
    if (prefixAutoSet) setField('invoice_prefix', autoPrefix(val));
  };

  const handlePrefixChange = (val) => {
    setField('invoice_prefix', val.toUpperCase());
    setPrefixAutoSet(false);
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModalError(null);
    setPrefixAutoSet(true);
    setShowCreate(true);
  };

  const closeCreate = () => {
    setShowCreate(false);
    setForm(EMPTY_FORM);
    setModalError(null);
    setPrefixAutoSet(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.facility_id) { setModalError('Please select a facility.'); return; }
    if (!form.name.trim()) { setModalError('Client name is required.'); return; }
    if (!form.invoice_prefix.trim()) { setModalError('Invoice prefix is required.'); return; }

    setSaving(true);
    setModalError(null);
    try {
      const res = await createClient(form);
      setClients(prev => [...prev, res.data]);
      navigate(`/clients/${res.data.client_id}`);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Clients</h1>
          <p>Track direct client accounts, fee schedules, invoices, and payments outside of insurance</p>
        </div>
        <button className="admin-create-btn" onClick={openCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Client
        </button>
      </div>

      <div className="admin-toolbar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="admin-search"
            placeholder="Search by name, facility, or prefix..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="admin-search-clear" onClick={() => setSearchQuery('')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <span className="admin-count">{filtered.length} {filtered.length === 1 ? 'client' : 'clients'}</span>
      </div>

      <div className="admin-table-card">
        {filtered.length === 0 ? (
          <div className="admin-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <h3>{searchQuery ? 'No clients match your search' : 'No clients yet'}</h3>
            <p>{searchQuery ? 'Try a different search term' : 'Add your first client to start tracking payments'}</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Facility</th>
                <th>Invoice Prefix</th>
                <th>Balance Due</th>
                <th>Last Payment</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(client => {
                const balance = parseFloat(client.total_billed ?? 0) - parseFloat(client.total_paid ?? 0);
                return (
                  <tr
                    key={client.client_id}
                    className="clients-row"
                    onClick={() => navigate(`/clients/${client.client_id}`)}
                  >
                    <td>
                      <div className="admin-name-cell">
                        <div className="admin-avatar clients-avatar">
                          {client.name.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#171363' }}>{client.name}</div>
                          {client.contact_name && (
                            <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: 1 }}>{client.contact_name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="clients-facility-badge">{client.facility_name}</span>
                    </td>
                    <td>
                      <span className="payer-code-badge">{client.invoice_prefix}</span>
                    </td>
                    <td>
                      <span className={`clients-balance ${balance > 0 ? 'positive' : 'zero'}`}>
                        {fmtCurrency(balance)}
                      </span>
                    </td>
                    <td>
                      {client.last_payment_date
                        ? <span className="admin-date-cell">{fmtDate(client.last_payment_date)}</span>
                        : <span className="payer-no-value">—</span>}
                    </td>
                    <td>
                      <span className={`admin-status-badge ${client.status}`}>
                        <span className="status-indicator" />
                        {client.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="payer-actions">
                        <button
                          className="admin-action-btn payer-edit-btn"
                          onClick={() => navigate(`/clients/${client.client_id}`)}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Client Modal */}
      {showCreate && (
        <div className="admin-modal-overlay">
          <div className="admin-modal clients-modal">
            <div className="admin-modal-header">
              <div className="admin-modal-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="admin-modal-header-text">
                <h2>Add Client</h2>
                <p>Create a new direct billing client account</p>
              </div>
            </div>

            {modalError && (
              <div className="admin-modal-alert error" style={{ marginBottom: 18 }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleSave} className="admin-modal-form">
              <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
                <label>Facility <span style={{ color: '#ef4444' }}>*</span></label>
                <select
                  className="admin-modal-select"
                  value={form.facility_id}
                  onChange={e => setField('facility_id', e.target.value)}
                >
                  <option value="">Select facility...</option>
                  {facilities.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>

              <div className="admin-modal-row">
                <div className="admin-modal-field" style={{ gridColumn: '1 / -1' }}>
                  <label>Client Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    className="admin-modal-input"
                    placeholder="e.g. Woman's Clinic"
                    autoFocus
                    value={form.name}
                    onChange={e => handleNameChange(e.target.value)}
                  />
                </div>
              </div>

              <div className="admin-modal-row">
                <div className="admin-modal-field">
                  <label>Invoice Prefix <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    className="admin-modal-input"
                    placeholder="e.g. WC"
                    maxLength={10}
                    value={form.invoice_prefix}
                    onChange={e => handlePrefixChange(e.target.value)}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    Used in invoice numbers: {form.invoice_prefix || 'PREFIX'}-2025-05
                  </span>
                </div>
                <div className="admin-modal-field">
                  <label>Payment Terms</label>
                  <input
                    type="text"
                    className="admin-modal-input"
                    placeholder="Net 30"
                    value={form.payment_terms}
                    onChange={e => setField('payment_terms', e.target.value)}
                  />
                </div>
              </div>

              <div className="admin-modal-row">
                <div className="admin-modal-field">
                  <label>Contact Name</label>
                  <input type="text" className="admin-modal-input" placeholder="Billing contact" value={form.contact_name} onChange={e => setField('contact_name', e.target.value)} />
                </div>
                <div className="admin-modal-field">
                  <label>Contact Phone</label>
                  <input type="text" className="admin-modal-input" placeholder="(555) 000-0000" value={form.contact_phone} onChange={e => setField('contact_phone', e.target.value)} />
                </div>
              </div>

              <div className="admin-modal-field">
                <label>Contact Email</label>
                <input type="email" className="admin-modal-input" placeholder="billing@client.com" value={form.contact_email} onChange={e => setField('contact_email', e.target.value)} />
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="admin-modal-btn-cancel" onClick={closeCreate}>Cancel</button>
                <button type="submit" className="admin-modal-btn-submit" disabled={saving}>
                  {saving && <span className="admin-modal-spinner" />}
                  {saving ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;
